// File: src/components/OmniCast/bus/omnicastReadEvents.ts
//
// OmniCast Read-Model Events (Phase 12.3 → 12.5)
// ----------------------------------------------
// OmniCast consumes ONLY omnicast:* events (witness boundary).
//
// Goals:
// - One canonical place for event names + payload shapes
// - Read-only contracts (no logic, no imports from OmniCine)
// - Compatible with OmniCastContextBus (omnicast:context / omnicast:npc:runtime / omnicast:timeline:phase)
//
// Notes:
// - "omnicast:context" is canonically emitted by OmniCastContextBus.
// - "omnicast:npc:runtime" and "omnicast:timeline:phase" are upstream OmniCast-safe feeds.
// - "omnicast:clock" is an optional mirror feed for telemetry/health overlays.
// - SurfaceIntent v1 is a witness-safe render intent contract (authority → witness).

/* ------------------------------------------------------------------ */
/* Event names                                                        */
/* ------------------------------------------------------------------ */

export const OMNICAST_CLOCK_EVENT = "omnicast:clock" as const;
export const OMNICAST_CONTEXT_EVENT = "omnicast:context" as const;
export const OMNICAST_NPC_RUNTIME_EVENT = "omnicast:npc:runtime" as const;
export const OMNICAST_TIMELINE_PHASE_EVENT = "omnicast:timeline:phase" as const;

/** Canonical surface-mode broadcast (UI-only, witness-safe) */
export const OMNICAST_SURFACE_MODE_EVENT = "omnicast:surface:mode" as const;

/** Authority → witness render intent (Phase 12.5 SurfaceIntent v1) */
export const OMNICAST_SURFACE_INTENT_EVENT = "omnicast:surface:intent" as const;

/** Witness-safe sensor transparency summary (no raw streams) */
export const OMNICAST_SENSORS_SUMMARY_EVENT = "omnicast:sensors:summary" as const;

/** Optional session binding broadcast (if you already use it elsewhere) */
export const OMNICAST_SESSION_EVENT = "omnicast:session" as const;

/* ------------------------------------------------------------------ */
/* Surface Mode (UI-only mirror)                                      */
/* ------------------------------------------------------------------ */

export type OmniCastSurfaceMode = "focus" | "balanced" | "ambient";

export interface OmniCastSurfaceModeDetail {
  mode: OmniCastSurfaceMode;

  /**
   * UI-level toggles (viewer preference). These do NOT grant authority.
   * Surfaces may blend this with SurfaceIntent recommendations.
   */
  toggles: { hud: boolean; ghosts: boolean };

  ts: number;

  /**
   * Source identifier for who/what emitted the mode change.
   * Keep "ui" as canonical default; allow future emitters.
   */
  source: "ui" | string;
}

/* ------------------------------------------------------------------ */
/* Surface Intent v1 (witness-safe intent contract)                    */
/* ------------------------------------------------------------------ */

/**
 * Overlay keys OmniCast can selectively enable/disable.
 * Narrow union preserves strong typing across surfaces.
 */
export type OmniCastSurfaceOverlayKey = "hud" | "ghosts" | "telemetry" | "sensors" | "provenance";

/**
 * Witness-safe rendering intent emitted by OmniCine authority.
 * Contains NO raw streams and NO edit authority — only display intent / UX hints.
 */
export type SurfaceIntentKind =
  | "cinematic" // filmic grading, gentle motion, minimal overlays
  | "clarity" // boost readability: contrast, captions, reduced bloom/ghosts
  | "safety" // emphasize safety: calmer tone, suppress intensity, hard gate ghosts
  | "analysis" // director/ops: show telemetry + provenance + markers
  | "ambient"; // background witness wall: low distraction, subtle context only

export type SurfaceMode = OmniCastSurfaceMode;

/**
 * Optional grade parameters (0..1 only).
 * IMPORTANT:
 * - ghostStrength01 is the canonical v1 field.
 * - ghost01 remains supported for backward compatibility (legacy surfaces),
 *   but should be phased out in favor of ghostStrength01.
 */
export interface SurfaceGradeV1 {
  warmth01?: number;
  contrast01?: number;
  vignette01?: number;

  bloom01?: number;
  grain01?: number;

  /** Canonical ghost strength hint (0..1). ON/OFF is overlays.ghosts. */
  ghostStrength01?: number;

  /** Back-compat alias (legacy). Prefer ghostStrength01. */
  ghost01?: number;
}

/** Overlay recommendations (witness-safe; no authority) */
export type SurfaceOverlaysV1 = Partial<Record<OmniCastSurfaceOverlayKey, boolean>>;

export interface SurfaceIntentV1 {
  sessionId?: string;

  /** v1 contract marker */
  v: 1;

  /** High-level intent for the witness surface */
  intent: SurfaceIntentKind;

  /**
   * Optional suggestion for page-local UI mode (still UI-owned).
   * OmniCast may blend with user prefs.
   */
  suggestMode?: SurfaceMode;

  /** 0..1 intensity hint (caller responsibility; downstream clamps) */
  intensity01?: number;

  /** Witness-safe overlay recommendations (no authority) */
  overlays?: SurfaceOverlaysV1;

  /** Witness-safe grade recommendations (0..1 only) */
  grade?: SurfaceGradeV1;

  /** Optional focus hint (0..1 rect); render-only. */
  focus?: {
    rect01?: { x: number; y: number; w: number; h: number };
    label?: string;
  };

  ts?: number; // epoch ms
  source?: string; // e.g. "timeline", "ui", "system"
}

/** Back-compat alias used in some files. Prefer SurfaceIntentV1 going forward. */
export type OmniCastSurfaceIntentDetailV1 = SurfaceIntentV1;

/**
 * Narrowing helper (optional; convenient in listeners).
 * Kept lightweight so it stays safe to call everywhere.
 */
export function isSurfaceIntentV1(d: unknown): d is SurfaceIntentV1 {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const x: any = d as any;
  return !!x && x.v === 1 && typeof x.intent === "string";
}

/* Optional dispatch helper (type-safe). Note: still a side effect; keep usage UI-only. */
export function dispatchOmniCastSurfaceIntent(detail: SurfaceIntentV1): void {
  window.dispatchEvent(new CustomEvent(OMNICAST_SURFACE_INTENT_EVENT, { detail }));
}

/* ------------------------------------------------------------------ */
/* Sensors Summary (witness-safe; no raw streams)                      */
/* ------------------------------------------------------------------ */

export interface OmniCastSensorsSummaryDetail {
  sessionId?: string;

  scope: "local" | "session" | "broadcast";
  activeSensors: string[];
  destinations: string[];

  ts?: number; // epoch ms
  source?: string;
}

/* ------------------------------------------------------------------ */
/* Clock (MediaClock mirror)                                          */
/* ------------------------------------------------------------------ */

export type ClockMode = "play" | "pause" | "buffer" | "idle";

export interface OmniCastClockDetail {
  sessionId?: string;
  mode: ClockMode;
  timeSec: number; // seconds-only
  ts?: number; // epoch ms (optional; enables latency)
  source?: "ingest" | "preview" | "stream";
}

/* ------------------------------------------------------------------ */
/* Timeline Phase (Narrative phase mirror)                            */
/* ------------------------------------------------------------------ */

export type OmniCastNarrativePhase = "setup" | "rising" | "peak" | "resolve";

export interface OmniCastTimelinePhaseDetail {
  sessionId?: string;

  /**
   * Accept string for forward-compat; downstream may normalize to OmniCastNarrativePhase.
   */
  phase?: OmniCastNarrativePhase | string;

  /**
   * 0..1 (caller responsibility; downstream clamps).
   */
  intensity?: number;

  ts?: number; // epoch ms
  source?: string;
}

/* ------------------------------------------------------------------ */
/* NPC Runtime (Classifier mirror)                                    */
/* ------------------------------------------------------------------ */

export type OmniCastNpcMode = "attention" | "emotion" | "overload" | "safety";

export interface OmniCastNpcRuntimeDetail {
  sessionId?: string;
  mode?: OmniCastNpcMode | string;
  probs?: {
    attention?: number;
    emotion?: number;
    overload?: number;
    safety?: number;
    [k: string]: number | undefined;
  };
  ts?: number; // epoch ms
  source?: string;
}

/* ------------------------------------------------------------------ */
/* Context (Normalized read-model emitted by OmniCastContextBus)       */
/* ------------------------------------------------------------------ */

export interface OmniCastContextDetail {
  sessionId?: string;
  ts: number;

  // Narrative
  phase?: OmniCastNarrativePhase;
  phase01?: number;

  // NPC affect (normalized)
  npcMode?: OmniCastNpcMode;
  npc01?: {
    attention: number;
    emotion: number;
    overload: number;
    safety: number;
  };

  // Recommended render params (0..1 only) — legacy ambient hints
  ambient?: {
    warmth01: number;
    contrast01: number;
    vignette01: number;
    ghost01: number;
  };

  // Optional: embedded surface intent snapshot (some buses may fold it in)
  surface?: {
    sessionId?: string;
    suggestMode?: OmniCastSurfaceMode;
    overlays?: SurfaceOverlaysV1;
    grade?: SurfaceGradeV1;
    intensity01?: number;
    ts?: number;
    source?: string;
  };
}

/* ------------------------------------------------------------------ */
/* Session (Optional binding signal)                                  */
/* ------------------------------------------------------------------ */

export interface OmniCastSessionDetail {
  sessionId: string;
  ts?: number;
  source?: string;
}

/* ------------------------------------------------------------------ */
/* Dispatch helpers (optional, type-safe)                              */
/* ------------------------------------------------------------------ */

export function dispatchOmniCastSurfaceMode(detail: OmniCastSurfaceModeDetail): void {
  window.dispatchEvent(new CustomEvent(OMNICAST_SURFACE_MODE_EVENT, { detail }));
}

export function dispatchOmniCastSensorsSummary(detail: OmniCastSensorsSummaryDetail): void {
  window.dispatchEvent(new CustomEvent(OMNICAST_SENSORS_SUMMARY_EVENT, { detail }));
}

export function dispatchOmniCastClock(detail: OmniCastClockDetail): void {
  window.dispatchEvent(new CustomEvent(OMNICAST_CLOCK_EVENT, { detail }));
}

export function dispatchOmniCastTimelinePhase(detail: OmniCastTimelinePhaseDetail): void {
  window.dispatchEvent(new CustomEvent(OMNICAST_TIMELINE_PHASE_EVENT, { detail }));
}

export function dispatchOmniCastNpcRuntime(detail: OmniCastNpcRuntimeDetail): void {
  window.dispatchEvent(new CustomEvent(OMNICAST_NPC_RUNTIME_EVENT, { detail }));
}

export function dispatchOmniCastContext(detail: OmniCastContextDetail): void {
  window.dispatchEvent(new CustomEvent(OMNICAST_CONTEXT_EVENT, { detail }));
}

export function dispatchOmniCastSession(detail: OmniCastSessionDetail): void {
  window.dispatchEvent(new CustomEvent(OMNICAST_SESSION_EVENT, { detail }));
}
